import { StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { autostoreTheme, ErrorBoundary, useToast } from "@qubit/autoparts";
import dayjs from "dayjs";
import i18n from "i18next";
import { useLDClient } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RouterProvider } from "react-router-dom";

import { applyRedirectOn401Response } from "~/api/shared";
import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { warehouseService, setupAuthInterceptor } from "~/api/warehouse";

import envConstants from "~/config/envConstants";
import { useFlag } from "~/config/flags";
import { setIsLoggingEnabled } from "~/features/supportBundle/supportBundle.slice";
import { useBeamer } from "~/hooks/useBeamer";
import { useClientConfigSync } from "~/hooks/useClientConfigSync";
import { useMessagePopper } from "~/hooks/useMessagePopper";
import { useSetLdClientId } from "~/hooks/useSetLdClientId";
import { useSetLdFcId } from "~/hooks/useSetLdFcId";
import useSetSentryTags from "~/hooks/useSetSentryTags";
import { useUserMessages } from "~/hooks/useUserMessages";
import { useValidateFulfillmentCenter } from "~/hooks/useValidateFulfillmentCenter";
import { useWebLock } from "~/hooks/useWebLock";
import { usePTLSubscription, useInitializeSignalRHubs } from "~/lib/signalr";
import { setPTLSimulationStateChange } from "~/redux/actions/ptl";
import { setLanguage } from "~/redux/actions/site";
import { StoreState } from "~/redux/reducers";
import {
  selectClientConfigReady,
  selectFlagsReadyClientId,
  selectServerConfig
} from "~/redux/selectors/appSelectors";
import { selectIsUserLoggedIn } from "~/redux/selectors/authSelectors";
import {
  selectLanguageCode,
  selectLocale
} from "~/redux/selectors/siteSelectors";
import { useGetClientConfigQuery } from "~/redux/warehouse/clientConfig.hooks";

import { router } from "./Root";
import {
  setAuthHeaderReady,
  setImprovedErrorHandlingConfigEnabled
} from "./app.slice";

import "~/lib/dayjs";

import { useAppSelector, useAppDispatch } from "./store";

const mapStateToProps = (state: StoreState) => ({
  clientId: state.login.profile ? getUserClientId(state.login.profile) : null,
  usersPort: state.workstations.siteWorkstation?.ports?.[0]?.portId ?? null,
  userId: state.login.profile?.userId || null,
  usersFulfillmentCenter: state.store.usersFulfillmentCenter,
  siteLanguageCode: state.site.languageCode,
  ptlSimulationEnabled: state.devTools.ptlSimulationEnabled
});

const connector = connect(mapStateToProps, {
  setPTLSimulationStateChange
});

type PropsFromRedux = ConnectedProps<typeof connector>;
type AppProps = PropsFromRedux;

function App(props: AppProps) {
  const {
    clientId,
    usersPort,
    usersFulfillmentCenter,
    userId,
    siteLanguageCode
  } = props;

  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const authHeaderReady = useAppSelector((state) => state.app.ready.authHeader);

  const locale = useAppSelector(selectLocale);
  const authMethod = useAppSelector((state) => state.login.authMethod);
  const stateAccessToken = useAppSelector((state) => state.login.accessToken);

  const dispatch = useAppDispatch();

  const isFlagsReady = useAppSelector(selectFlagsReadyClientId);
  const { isSupportBundleEnabled, improvedErrorHandlingConfig } = useFlag();

  const { t } = useTranslation();

  const shouldFetchClientConfig = isFlagsReady && clientId && authHeaderReady;

  useGetClientConfigQuery(undefined, {
    skip: !shouldFetchClientConfig
  });

  const { errorToast } = useToast();

  const { parseError: configParseError } = useClientConfigSync();

  useEffect(() => {
    dispatch(
      setImprovedErrorHandlingConfigEnabled(improvedErrorHandlingConfig)
    );
  }, [improvedErrorHandlingConfig, dispatch]);

  useEffect(() => {
    if (improvedErrorHandlingConfig) return;
    if (configParseError) {
      errorToast(configParseError);
    }
  }, [configParseError, errorToast, improvedErrorHandlingConfig]);

  const localeText = {
    cancelButtonLabel: t("cancel"),
    okButtonLabel: t("confirm")
  };

  const sentryTags = {
    client: clientId,
    port: usersPort,
    fulfillmentCenter: usersFulfillmentCenter?.fulfillmentCenterId || null,
    userId: userId,
    clientRelease: envConstants.VERSION_TAG || envConstants.VERSION
  };

  useUserMessages();
  useWebLock();

  // launch darkly setup
  const ldClient = useLDClient();
  // set the clientId if fc is not defined
  useSetLdClientId({
    clientId,
    ldClient,
    fcId: usersFulfillmentCenter?.fulfillmentCenterId
  });
  // set both the client id and fc id if both are defined
  useSetLdFcId({
    clientId,
    ldClient,
    fcId: usersFulfillmentCenter?.fulfillmentCenterId
  });

  // auth inteceptor setup
  useEffect(() => {
    const initializeInterceptor = async () => {
      if (!authMethod || !isUserLoggedIn) return;

      const success = await setupAuthInterceptor({
        authMethod,
        stateAccessToken
      });

      if (success) {
        applyRedirectOn401Response(warehouseService, dispatch);
        dispatch(setAuthHeaderReady(true));
      }
    };

    void initializeInterceptor();
  }, [authMethod, isUserLoggedIn, stateAccessToken, dispatch]);

  const { validateFc } = useValidateFulfillmentCenter();

  useEffect(() => {
    if (isUserLoggedIn && authHeaderReady) {
      void validateFc();
    }
  }, [isUserLoggedIn, authHeaderReady, validateFc]);

  useEffect(() => {
    if (envConstants.RUNTIME_ENVIRONMENT !== "production") return;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (userId) mixpanel.identify(userId);
  }, [userId]);

  // Sync feature flag to Redux for access outside of hooks
  useEffect(() => {
    if (isFlagsReady) {
      dispatch(setIsLoggingEnabled(isSupportBundleEnabled));
    }
  }, [isFlagsReady, isSupportBundleEnabled, dispatch]);

  useBeamer();

  // old redux notifications
  useMessagePopper();

  // do not run if no fulfillment center is selected
  useInitializeSignalRHubs({
    usersFulfillmentCenterId: usersFulfillmentCenter?.fulfillmentCenterId
  });

  usePTLSubscription(
    props.setPTLSimulationStateChange,
    envConstants.ENABLE_PTL_SIMULATION === "true" && props.ptlSimulationEnabled
  );

  useSetSentryTags(sentryTags);

  const serverConfig = useAppSelector(selectServerConfig);
  const clientConfigReady = useAppSelector(selectClientConfigReady);
  const persistedLanguageCode = useAppSelector(selectLanguageCode);

  useEffect(() => {
    if (
      clientConfigReady &&
      !persistedLanguageCode &&
      serverConfig?.site_defaultLanguageCode
    ) {
      dispatch(setLanguage(serverConfig.site_defaultLanguageCode));
    }
  }, [clientConfigReady, persistedLanguageCode, serverConfig, dispatch]);

  useEffect(() => {
    void i18n.changeLanguage(siteLanguageCode);
    dayjs.locale(locale);
  }, [siteLanguageCode, locale]);

  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={locale}
        localeText={localeText}
      >
        <ErrorBoundary>
          <div
            id="App"
            style={{
              background: autostoreTheme.palette.background.default,
              minHeight: "100vh"
            }}
          >
            <RouterProvider
              router={router}
              future={{
                // uses useTransition instead of useState for updates. avoid UI blocking.
                v7_startTransition: true
              }}
            />
          </div>
        </ErrorBoundary>
      </LocalizationProvider>
    </StyledEngineProvider>
  );
}

export default connector(App);
